// Work — the portfolio as collections.
//
// Every photo already carries an `event` tag the admin uploader sets; that tag
// is the folder. The page opens on one card per folder (cover, name, count),
// and opening a folder shows its grid. So "Paws & Portraits" appears here the
// moment someone uploads a few selects tagged that way — no new table, no new
// admin screen. A folder is linkable as /work?c=<tag>.
//
// Video-first in structure: an item with a `video_url` renders its poster with
// a play affordance and mounts the embed only on click.

import React, { useEffect, useMemo, useState } from 'react';
import { Bay, Btn, Eyebrow, Frame, Head, OtherDoor } from '../primitives.jsx';
import { useSiteData, workPhotos, workTags } from '../data.js';
import { go } from '../routes.js';

function label(tag) {
  return String(tag).replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function embedFor(url) {
  const yt = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/.exec(url || '');
  if (yt) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0` };
  const vm = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url || '');
  if (vm) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
  return { kind: 'video', src: url };
}

function Item({ item }) {
  const [playing, setPlaying] = useState(false);
  const hasVideo = !!item.video_url;

  if (hasVideo && playing) {
    const e = embedFor(item.video_url);
    return (
      <div className="hddn-frame hddn-frame--tall">
        {e.kind === 'iframe' ? (
          <iframe src={e.src} title={item.caption || 'HDDN LAB video'} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen style={{ width: '100%', height: '100%', border: 0 }} />
        ) : (
          <video src={e.src} controls autoPlay playsInline poster={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </div>
    );
  }

  const frame = (
    <Frame src={item.url} alt={item.caption || 'Work made at HDDN LAB'} ratio="tall">
      {hasVideo ? <span className="hddn-frame__play" aria-hidden="true"><span>&#9654;</span></span> : null}
    </Frame>
  );
  if (hasVideo) {
    return (
      <button onClick={() => setPlaying(true)} style={{ display: 'block', width: '100%', padding: 0, textAlign: 'left' }} aria-label={`Play ${item.caption || 'video'}`}>
        {frame}
      </button>
    );
  }
  // A live collection item (a Paws & Portraits pet) links through to its own
  // page, where the family's story and the rest of the set live.
  if (item.link) {
    return (
      <a href={item.link} style={{ display: 'block' }} aria-label={`${item.caption} — open their page`}>
        {frame}
        <span className="hddn-folder__n" style={{ display: 'block' }}>{item.caption}</span>
      </a>
    );
  }
  return frame;
}

function readCollection() {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('c') || null;
}

export default function Work() {
  const d = useSiteData();
  const all = workPhotos(d.photos);
  const tags = workTags(d.photos);
  const [active, setActive] = useState(readCollection);

  // Keep the folder in the URL so it can be shared, without a history entry
  // per click.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (active) url.searchParams.set('c', active); else url.searchParams.delete('c');
    window.history.replaceState({}, '', url);
  }, [active]);

  const folders = useMemo(() => tags.map((t) => {
    const items = all.filter((p) => p.event === t);
    return { tag: t, name: label(t), cover: items[0], count: items.length, videos: items.filter((p) => p.video_url).length };
  }), [all, tags]);

  const open = active ? folders.find((f) => f.tag === active) : null;
  const shown = open ? all.filter((p) => p.event === open.tag) : [];

  return (
    <>
      <Bay band="ground" tight>
        <Eyebrow>{open ? 'Gallery' : 'Galleries'}</Eyebrow>
        <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '16ch' }}>{open ? open.name : 'Photos and videos, created here.'}</h1>
        <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '50ch' }}>
          {open
            ? `${open.count} ${open.count === 1 ? 'piece' : 'pieces'}${open.videos ? `, ${open.videos} on video` : ''}. Shot by HDDN LAB at 72 Centre Avenue and around it.`
            : 'View galleries from projects we have done: portrait sessions, Paws & Portraits, client shoots, events. Open one to see the set.'}
        </p>
        {open ? (
          <div style={{ marginTop: 22 }}>
            <Btn onClick={() => setActive(null)} variant="line">&larr; All collections</Btn>
          </div>
        ) : null}
      </Bay>

      <Bay>
        {open ? (
          shown.length ? (
            <>
              <p className="hddn-vis-hidden" aria-live="polite">{shown.length} items shown</p>
              <div className="hddn-grid hddn-grid--3">
                {shown.map((p) => <Item key={p.id} item={p} />)}
              </div>
            </>
          ) : (
            <div className="hddn-alert">
              <p className="hddn-h4">That collection is empty.</p>
              <p className="hddn-muted" style={{ marginTop: 8 }}>
                <button onClick={() => setActive(null)} style={{ color: 'var(--accent-ink)', textDecoration: 'underline', padding: 0 }}>Back to all collections</button>.
              </p>
            </div>
          )
        ) : folders.length ? (
          <div className="hddn-folders" role="list">
            {folders.map((f) => (
              <button key={f.tag} className="hddn-folder" role="listitem" onClick={() => setActive(f.tag)} aria-label={`Open ${f.name}, ${f.count} pieces`}>
                <Frame src={f.cover && f.cover.url} alt="" ratio="tall">
                  {f.videos ? <span className="hddn-frame__play" aria-hidden="true"><span>&#9654;</span></span> : null}
                </Frame>
                <span className="hddn-folder__t" style={{ display: 'block' }}>{f.name}</span>
                <span className="hddn-folder__n" style={{ display: 'block' }}>{f.count} {f.count === 1 ? 'piece' : 'pieces'}{f.videos ? ` · ${f.videos} video` : ''}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="hddn-alert">
            <p className="hddn-h4">Nothing here yet.</p>
            <p className="hddn-muted" style={{ marginTop: 8 }}>
              Collections appear as work goes up. In the meantime the numbers on the{' '}
              <a href="/content" onClick={go('/content')} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>content page</a>{' '}
              are the clearest picture of what we make.
            </p>
          </div>
        )}
      </Bay>

      <Bay band="raised">
        <div className="hddn-grid hddn-grid--sidebar">
          <Head eyebrow="Every one of these" title="Was made by the same people." />
          <div style={{ display: 'grid', gap: 12, alignContent: 'end' }}>
            <Btn href="/content" onClick={go('/content')} variant="fill" wide>Content creation</Btn>
            <Btn href="/photographer" onClick={go('/photographer')} variant="line" wide>Book a photographer</Btn>
          </div>
        </div>
      </Bay>

      <OtherDoor
        k="Curious about the space"
        title="You can rent it yourself."
        body="Hourly and day rates, gear included, five minutes from Metro-North."
        href="/studio"
        cta="See the studio"
      />
    </>
  );
}
